/**
 * Shared request/response envelopes.
 *
 * S01 asks for normalized errors and a single FE data layer; these are the
 * shapes both sides agree on. Feature DTOs arrive with their phases — this file
 * only holds what is cross-cutting.
 */
import { z } from 'zod';

/** ULID — our correlation id. 26 chars, Crockford base32, no I/L/O/U. */
export const ulidSchema = z
  .string()
  .length(26)
  .regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'must be a valid ULID');

/**
 * The normalized error envelope every non-2xx response uses.
 *
 * `code` is a stable machine-readable token the web app can branch on; `message`
 * is safe to show a user. `details` carries field-level validation failures.
 */
export const apiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
  /** Echoes the request's correlation id so a user report maps to a log line. */
  requestId: z.string().optional(),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

/** Cursor-free page request. S08 needs a page-size control, so both are explicit. */
export const pageQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(10),
});
export type PageQuery = z.infer<typeof pageQuerySchema>;

/** Paged response wrapper. `total` drives S08's "1–10 of 47" indicator. */
export function pagedSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
  });
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** Health payload behind S03's connection-status indicator. */
export const healthSchema = z.object({
  status: z.enum(['ok', 'degraded', 'down']),
  version: z.string(),
  checks: z.object({
    database: z.enum(['ok', 'down']),
    queue: z.enum(['ok', 'down']),
    sirma: z.enum(['ok', 'degraded', 'down', 'unconfigured']),
    /** The identity provider — the dependency that breaks sign-in (S02). */
    idp: z.enum(['ok', 'degraded', 'down', 'unconfigured']),
  }),
  checkedAt: z.string().datetime(),
});
export type Health = z.infer<typeof healthSchema>;
